/**
 * The last step before Stripe Checkout: the traveller asks for the plan to
 * start now and acknowledges what withdrawing within 14 days costs (Art. 16(a)
 * Consumer Rights Directive). Mounted once in App; services/stripe.ts asks it
 * through utils/immediateStart.ts before every checkout. The box is not
 * pre-ticked, and the payment button stays off until it is ticked.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { useI18nStore } from "@/stores/i18nStore";
import {
  IMMEDIATE_START_EVENT,
  type ImmediateStartRequest,
} from "@/utils/immediateStart";
import React, { useEffect, useState } from "react";

export const ImmediateStartDialog: React.FC = () => {
  const { t } = useI18nStore();
  const [request, setRequest] = useState<ImmediateStartRequest | null>(null);
  const [ticked, setTicked] = useState(false);

  useEffect(() => {
    const onAsk = (event: Event) => {
      setTicked(false);
      setRequest((event as CustomEvent<ImmediateStartRequest>).detail);
    };
    window.addEventListener(IMMEDIATE_START_EVENT, onAsk);
    return () => window.removeEventListener(IMMEDIATE_START_EVENT, onAsk);
  }, []);

  if (!request) return null;

  const answer = (confirmed: boolean) => {
    request.resolve(confirmed);
    setRequest(null);
  };
  const tripPlan = request.tier.endsWith("_SINGLE");

  return (
    <Dialog open onOpenChange={() => answer(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.immediateStartTitle}</DialogTitle>
          {!tripPlan && (
            <DialogDescription>{t.immediateStartTrial}</DialogDescription>
          )}
        </DialogHeader>
        <div className="flex items-start gap-3">
          <input
            id="immediate-start"
            type="checkbox"
            checked={ticked}
            onChange={(e) => setTicked(e.target.checked)}
            className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-primary-600"
          />
          <label
            htmlFor="immediate-start"
            className="cursor-pointer text-sm leading-normal text-slate-700"
          >
            {t.immediateStartAck}
          </label>
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => answer(false)}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            {t.immediateStartBack}
          </button>
          <button
            type="button"
            disabled={!ticked}
            onClick={() => answer(true)}
            className="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t.immediateStartContinue}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImmediateStartDialog;
